import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Chip,
} from "@mui/material";
import CheckIcon from "@material-ui/icons/Check";
import CachedIcon from "@material-ui/icons/Cached";
import ErrorIcon from "@material-ui/icons/Error";
import dateFormat from "dateformat";
import axios from "axios";

function getStatusIcon(item) {
  switch (item.status) {
    case "approved":
      return <CheckIcon style={{ color: "green" }} />;
    case "rejected":
      return <ErrorIcon style={{ color: "red" }} />;
    case "pending":
    default:
      return <CachedIcon style={{ color: "orange" }} />;
  }
}

export default function ScheduleDiscrepancy({ isBank, loan_information }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [pendingApproval, setPendingApproval] = useState(null);

  const allDiscrepancies = [
    ...(loan_information.schedule_discrepancies?.missing_dates || []).map((d) => ({
      type: "missing_date",
      date: d.date || d,
      status: d.status || "pending",
      comment: d.comment,
      reviewed_by: d.reviewed_by,
      review_date: d.review_date,
    })),
    ...(loan_information.schedule_discrepancies?.mismatched_rows || []).map((r) => ({
      type: "mismatched_row",
      date: r.payment_date,
      payment_amount: r.payment_amount,
      schedule_amount: r.schedule_amount,
      difference: r.payment_amount - r.schedule_amount,
      status: r.status || "pending",
      comment: r.comment,
      reviewed_by: r.reviewed_by,
      review_date: r.review_date,
    })),
  ];

  if (!allDiscrepancies.length) return null;

  const pendingDiscrepancies = allDiscrepancies.filter((i) => i.status === "pending");
  const reviewedDiscrepancies = allDiscrepancies.filter((i) => i.status !== "pending");

  const handleApprovalWithComment = (applicationID, approval, comment) => {
    const component = window.location.pathname.split("/")[1];
    axios
      .post("/agency/approve_discrepancy", {
        application: applicationID,
        component,
        approval,
        comment,
      })
      .then((res) => {
        alert(res.data.message);
        window.location.reload();
      })
      .catch((err) => {
        alert(err.response?.data?.message || "შეცდომა");
        console.error(err);
      });
  };

  const openCommentDialog = (approval) => {
    setPendingApproval(approval);
    setDialogOpen(true);
  };
  const handleDialogSubmit = () => {
    if (!comment.trim()) {
      alert("კომენტარი აუცილებელია");
      return;
    }
    handleApprovalWithComment(loan_information._id, pendingApproval, comment);
    setDialogOpen(false);
    setComment("");
  };
  const handleDialogCancel = () => {
    setDialogOpen(false);
    setComment("");
  };

  const renderDiscrepancyTable = (discrepancies, isPending) => (
    <TableContainer component={Paper} elevation={1} sx={{ width: "100%", overflowX: "auto", mb: 2 }}>
      <Table size="small" sx={{ width: "100%" }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: isPending ? "#fff3e0" : "#f5f5f5" }}>
            <TableCell>
              <strong>ტიპი</strong>
            </TableCell>
            <TableCell>
              <strong>თარიღი</strong>
            </TableCell>
            <TableCell align="right">
              <strong>გადახდის რაოდენობა</strong>
            </TableCell>
            <TableCell align="right">
              <strong>გრაფიკის რაოდენობა</strong>
            </TableCell>
            <TableCell align="right">
              <strong>განსხვავება</strong>
            </TableCell>
            <TableCell align="center">
              <strong>სტატუსი</strong>
            </TableCell>
            <TableCell>
              <strong>კომენტარი</strong>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {discrepancies.map((item, idx) => (
            <TableRow
              key={idx}
              sx={{
                backgroundColor: isPending ? (idx % 2 === 0 ? "#fff9c4" : "#ffffff") : idx % 2 === 0 ? "#f9f9f9" : "#ffffff",
                opacity: isPending ? 1 : 0.8,
              }}
            >
              <TableCell>
                <Chip
                  label={item.type === "missing_date" ? "გამოტოვებული თარიღი" : "შეუსაბამისობა"}
                  size="small"
                  color={item.type === "missing_date" ? "warning" : "error"}
                  variant="outlined"
                />
              </TableCell>
              <TableCell>{dateFormat(item.date, "dd/mm/yyyy")}</TableCell>
              <TableCell align="right">{item.type === "mismatched_row" ? item.payment_amount : "-"}</TableCell>
              <TableCell align="right">{item.type === "mismatched_row" ? item.schedule_amount : "-"}</TableCell>
              <TableCell align="right">
                {item.type === "mismatched_row" ? (
                  <span
                    style={{
                      color: item.difference > 0 ? "#2e7d32" : item.difference < 0 ? "#d32f2f" : "#666",
                      fontWeight: "bold",
                    }}
                  >
                    {item.difference > 0 ? "+" : ""}
                    {item.difference}
                  </span>
                ) : (
                  "-"
                )}
              </TableCell>
              <TableCell align="center">
                <Chip
                  icon={getStatusIcon(item)}
                  label={item.status === "approved" ? "დამტკიცებული" : item.status === "rejected" ? "უარყოფილი" : "მოლოდინში"}
                  size="small"
                  color={item.status === "approved" ? "success" : item.status === "rejected" ? "error" : "warning"}
                  variant={isPending ? "filled" : "outlined"}
                />
              </TableCell>
              <TableCell>
                {item.comment ? (
                  <Typography variant="body2" sx={{ maxWidth: 200, wordBreak: "break-word" }}>
                    {item.comment}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary" fontStyle="italic">
                    კომენტარი არ არის
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ my: 4, width: "100%" }}>
      <Card elevation={0}>
        <CardHeader title={<Typography variant="h6">შეუსაბამისობები ({allDiscrepancies.length})</Typography>} sx={{ pb: 1 }} />
        <CardContent sx={{ pt: 0 }}>
          {pendingDiscrepancies.length > 0 && (
            <>
              <Typography variant="subtitle1" color="error.main" gutterBottom>
                განხილვის მოლოდინში ({pendingDiscrepancies.length})
              </Typography>
              {renderDiscrepancyTable(pendingDiscrepancies, true)}
              {!isBank && (
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                  <Button
                    variant="outlined"
                    color="success"
                    startIcon={<CheckIcon />}
                    onClick={() => openCommentDialog(true)}
                    sx={{ textTransform: "none", borderRadius: 1 }}
                  >
                    დადასტურება
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<ErrorIcon />}
                    onClick={() => openCommentDialog(false)}
                    sx={{ textTransform: "none", borderRadius: 1 }}
                  >
                    უარყოფა
                  </Button>
                </Box>
              )}
            </>
          )}

          {reviewedDiscrepancies.length > 0 && (
            <>
              <Typography variant="subtitle1" color="primary.main" gutterBottom>
                განხილული ({reviewedDiscrepancies.length})
              </Typography>
              {renderDiscrepancyTable(reviewedDiscrepancies, false)}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={handleDialogCancel} maxWidth="sm" fullWidth>
        <DialogTitle>{pendingApproval ? "დადასტურების კომენტარი" : "უარყოფის კომენტარი"}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="კომენტარი"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="გთხოვთ მიუთითოთ კომენტარი შეუსაბამისობის შესახებ..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogCancel} sx={{ textTransform: "none", borderRadius: 1 }}>
            გაუქმება
          </Button>
          <Button onClick={handleDialogSubmit} variant="contained" sx={{ textTransform: "none", borderRadius: 1 }}>
            დადასტურება
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
